import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  MouseEvent as MouseEventHendler,
} from 'react'

import { NotificationCellProps } from '@knocklabs/react'
import { ContentBlock, MarkdownContentBlock, TextContentBlock } from '@knocklabs/client'
import { formatTimestamp, useTranslations } from '@knocklabs/react-core'
import { useKnockFeed } from '@knocklabs/react-core'

import FirestoreService from '../../services/firestoreService'

import useToasterHelper from '../../helpers/ToasterHelper'
import { getPaymentRequestStatus } from '../../helpers/payment'

import { useAppSelector } from '../../store/hooks'
import { selectProfileData } from '../../store/user/userSlice'

import { IMAGE_CONSTS } from '../../const/image-const'
import { MESSAGES_CONST } from '../../const/messages-const'
import { CONST } from '../../const/const'
import { Loader2Icon } from 'lucide-react'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

type BlockByName = {
  [name: string]: ContentBlock
}

function maybeNavigateToUrlWithDelay(url?: string) {
  if (url && url !== '') {
    setTimeout(() => window.location.assign(url), 200)
  }
}

const NotificationCell = ({ item, onItemClick }: NotificationCellProps) => {
  const toastFunctions = useToasterHelper()

  const profileData = useAppSelector(selectProfileData)

  const actor = item.actors?.[0]
  const { locale } = useTranslations()
  const { feedClient } = useKnockFeed()

  // Track local state for read status
  const [localItem, setLocalItem] = useState(item)
  const [approveLoading, setApproveLoading] = useState(false)
  const [rejectLoading, setRejectLoading] = useState(false)

  // Update local state when item prop changes
  useEffect(() => {
    setLocalItem(item)
  }, [item])

  // Add a parent container ref to track both button and modal
  const buttonRef = useRef<HTMLButtonElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const [openModal, setOpenModal] = useState(false)

  const blocksByName: BlockByName = useMemo(() => {
    return localItem.blocks.reduce((acc, block) => {
      return { ...acc, [block.name]: block }
    }, {})
  }, [localItem])
  const actionUrl = (blocksByName.action_url as TextContentBlock)?.rendered

  const onContainerClickHandler = useCallback(() => {
    // Mark as interacted + read once we click the item
    feedClient.markAsInteracted(localItem, {
      type: 'cell_click',
      action: actionUrl,
    })

    if (onItemClick) return onItemClick(localItem)

    return maybeNavigateToUrlWithDelay(actionUrl)
  }, [localItem, actionUrl, onItemClick, feedClient])

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      // Get the element the mouse is moving to
      const relatedTarget = event.relatedTarget as Node

      // Check if mouse is moving to either the button or modal
      const isMovingToButton = buttonRef.current?.contains(relatedTarget)
      const isMovingToModal = modalRef.current?.contains(relatedTarget)

      // Only close if not moving to either element
      if (!isMovingToButton && !isMovingToModal) {
        setOpenModal(false)
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isClickInsideButton = buttonRef.current?.contains(target)
      const isClickInsideModal = modalRef.current?.contains(target)

      if (!isClickInsideButton && !isClickInsideModal) {
        setOpenModal(false)
      }
    }

    // Add mouseleave listeners to both button and modal
    const buttonElement = buttonRef.current
    const modalElement = modalRef.current

    if (buttonElement) {
      buttonElement.addEventListener('mouseleave', handleMouseLeave)
    }
    if (modalElement) {
      modalElement.addEventListener('mouseleave', handleMouseLeave)
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      if (buttonElement) {
        buttonElement.removeEventListener('mouseleave', handleMouseLeave)
      }
      if (modalElement) {
        modalElement.removeEventListener('mouseleave', handleMouseLeave)
      }
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [buttonRef.current, modalRef.current]) // Add refs to dependency array to handle dynamic updates

  // Function to update the notification's read state
  const updateNotificationReadState = async (markAsReadFlag: boolean) => {
    try {
      if (markAsReadFlag) {
        await feedClient?.markAsRead(localItem)
        // Update local state to reflect the change
        setLocalItem((prev) => ({
          ...prev,
          read_at: new Date().toISOString(),
        }))
      } else {
        await feedClient?.markAsUnread(localItem)
        // Update local state to reflect the change
        setLocalItem((prev) => ({
          ...prev,
          read_at: null,
        }))
      }
    } catch (error) {
      alert('Failed to update notification status:')
    }
  }

  const onApprove = async (event: MouseEventHendler<HTMLButtonElement>, isApprove: boolean) => {
    try {
      if (isApprove) {
        setApproveLoading(true)
      } else {
        setRejectLoading(true)
      }

      event.stopPropagation()
      feedClient?.markAsRead(localItem)

      if (localItem.data?.pmId && profileData?.id) {
        const paymentMethodsAccessUpdated = profileData.paymentMethodsAccess?.[
          localItem.data.pmId
        ]?.map((item) => {
          if (item.requesterId === actor?.id) {
            return { ...item, status: isApprove ? 'approved' : 'rejected' }
          } else {
            return item
          }
        })

        await FirestoreService.updateItem(COLLECTIONS.USERS.NAME, profileData?.id, {
          paymentMethodsAccess: {
            ...profileData.paymentMethodsAccess,
            [localItem.data.pmId]: paymentMethodsAccessUpdated,
          },
        })

        if (isApprove) {
          toastFunctions.success({
            message: 'Approved successfully',
          })
        } else {
          toastFunctions.success({
            message: 'Rejected successfully',
          })
        }

        setOpenModal(false)
      }
    } catch (error: any) {
      console.error('error: ', error)
      toastFunctions.error({
        message: error?.response?.data?.error ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setApproveLoading(false)
      setRejectLoading(false)
    }
  }

  const paymentRequestStatus = getPaymentRequestStatus(localItem.data?.pmId, actor?.id, profileData)

  return (
    <div className="relative">
      <div
        onClick={onContainerClickHandler}
        className="flex justify-between gap-3 items-center w-full text-SeabiscuitDark200ThemeColor text-[14px] py-8 hover:bg-[#F1F3F8] px-4 group border-t border-b-1 border-[#D3DAEE]"
      >
        <div className="w-[25%] flex flex-row items-center gap-2 flex-shrink-0 rounded-[12px]">
          <img
            src={
              actor && 'image' in actor && actor.image
                ? actor.image
                : IMAGE_CONSTS.PLACEHOLDERS.USER
            }
            onError={(e) => ((e.target as any).src = IMAGE_CONSTS.PLACEHOLDERS.USER)}
            className="w-11 h-11 rounded-full object-cover bg-green-200"
            alt="sender"
          />

          <span className="capitalize text-ellipsis overflow-hidden whitespace-nowrap font-bold">
            {actor && 'name' in actor ? actor.name : 'no name'}
          </span>
        </div>

        <div className="flex-1 flex-col items-center rounded-[12px] text-ellipsis h-full overflow-hidden">
          <span className=" text-[#122B4666]/[.4] text-[12px]">
            {formatTimestamp(localItem.inserted_at, { locale })}
          </span>
          {blocksByName.body && (
            <div
              className="capitalize text-ellipsis overflow-hidden whitespace-nowrap text-[14px]"
              dangerouslySetInnerHTML={{
                __html: (blocksByName.body as MarkdownContentBlock).rendered,
              }}
            />
          )}
        </div>

        <div className="flex flex-row justify-end flex-shrink-0 w-[200px]">
          <div className="hidden group-hover:flex group-hover:justify-end">
            <button
              ref={buttonRef}
              onClick={(event) => {
                event.stopPropagation()
                setOpenModal(true)
              }}
              className="inline-flex w-[100px] h-[45px] mx-auto items-center justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-[14px] text-SeabiscuitDark200ThemeColor bg-transparent focus:outline-none ring-2 ring-offset-2 ring-[#D3DAEE]"
            >
              Manage
            </button>
          </div>
          <div className="flex justify-end items-center p-2 group-hover:hidden">
            {!localItem.read_at && <div className="w-3 h-3 bg-[#F7074F] rounded-full"></div>}
          </div>
        </div>
      </div>
      {openModal && (
        <div
          ref={modalRef}
          className="absolute bg-white border border-gray-200 rounded-lg shadow-lg z-50 right-0 top-[50%]"
        >
          {paymentRequestStatus === 'pending' && (
            <>
              <button
                disabled={approveLoading}
                onClick={(e) => onApprove(e, true)}
                className="flex gap-2 items-center w-full text-left px-4 py-2 text-SeabiscuitGreenThemeColor hover:bg-gray-100"
              >
                {approveLoading && <Loader2Icon className="animate-spin w-4" />}
                Approve request
              </button>
              <button
                disabled={rejectLoading}
                onClick={(e) => onApprove(e, false)}
                className="flex gap-2 items-center w-full text-left px-4 py-2 text-SeabiscuitMainThemeColor hover:bg-gray-100"
              >
                {rejectLoading && <Loader2Icon className="animate-spin w-4" />}
                Reject request
              </button>
            </>
          )}
          <button
            onClick={async (event) => {
              event.stopPropagation()
              if (!localItem.read_at) {
                await updateNotificationReadState(true)
              } else {
                await updateNotificationReadState(false)
              }
              setOpenModal(false)
            }}
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            {localItem.read_at ? 'Mark as Unread' : 'Mark as Read'}
          </button>
          <button
            onClick={(event) => {
              event.stopPropagation()
              feedClient?.markAsArchived(localItem)
              setOpenModal(false)
            }}
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

export default NotificationCell
